// Sequência de eventos (SOE). Duas formas de ver, como na oscilografia:
//   - Arquivos (padrão): a lista dos arquivos de eventos recebidos; escolhido um, os eventos
//     dele, na ordem do arquivo;
//   - Busca por período: todos os eventos de todos os arquivos, filtráveis por período,
//     regulador e texto. Os sem hora válida (relógio do regulador desacertado) ficam numa aba
//     própria, na ordem do arquivo — a ordem ainda informa, o instante não.

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Clock, FileText, Trash2, Waves } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { DeleteFilesDialog, FileTrashPage, type TrashKind } from "../components/FileTrash";
import { Alert, Badge, Button, Card, EmptyState, Input, PageHeader, Select, Spinner, Table, cx } from "../components/ui";
import { api } from "../lib/api";
import { useTree } from "../lib/hooks";
import { canManageAnything, useMe } from "../lib/session";
import { formatDateTimeMs } from "../lib/units";

const PERIODS = [
  { key: "24h", label: "24 horas", ms: 86400_000 },
  { key: "7d", label: "7 dias", ms: 7 * 86400_000 },
  { key: "30d", label: "30 dias", ms: 30 * 86400_000 },
  { key: "365d", label: "12 meses", ms: 365 * 86400_000 },
];

interface Row {
  id: number;
  ts?: string;
  device_time?: string;
  number: number;
  tag: string;
  description: string;
  state: boolean | null;
  file: string;
  raw?: string;
  line_no?: number;
  site_name: string;
  unit_name: string;
  regulator_name: string;
}

function useRegulatorOptions() {
  const { data: tree } = useTree();
  return useMemo(
    () => tree?.groups.flatMap((g) => g.sites.flatMap((s) => s.units.flatMap((u) => u.regulators.map((r) => ({ id: r.id, label: `${s.name} › ${u.name} › ${r.name}` }))))) ?? [],
    [tree],
  );
}

export function SoePage() {
  const [params, setParams] = useSearchParams();
  const view = params.get("aba") === "busca" ? "search" : "files";
  return (
    <>
      <PageHeader title="Sequência de eventos" description="Eventos registrados pelos reguladores, com a hora do relógio de cada um." />
      <div className="mb-4 inline-flex rounded-md border border-line bg-surface p-0.5" role="tablist">
        <TabBtn on={view === "files"} onClick={() => setParams({}, { replace: true })}>
          Arquivos
        </TabBtn>
        <TabBtn on={view === "search"} onClick={() => setParams({ aba: "busca" }, { replace: true })}>
          Busca por período
        </TabBtn>
      </div>
      {view === "files" ? <SoeFiles /> : <SoeSearch />}
    </>
  );
}

// ── Arquivos ─────────────────────────────────────────────────────────────────

interface SoeFileRow {
  id: number;
  name: string;
  received_at: string;
  decode_error: string | null;
  events: number;
  timed: number;
  activations: number;
  first_ts: string | null;
  last_ts: string | null;
  /** Gestor da usina: pode mandar para a lixeira. */
  can_delete: boolean;
  regulator_id: number;
  regulator_name: string;
  unit_name: string;
  site_name: string;
}

const EVT_TRASH: TrashKind = {
  api: "/api/soe/files",
  // Apagar um arquivo tira os eventos dele também da busca por período e da oscilografia.
  invalidate: [["soe-files"], ["soe-file"], ["soe"], ["soe-untimed"], ["osc-detail"]],
  one: "arquivo de eventos",
  many: "arquivos de eventos",
  listPath: "/eventos",
  listLabel: "Sequência de eventos",
};

export function SoeTrashPage() {
  return <FileTrashPage kind={EVT_TRASH} title="Lixeira de arquivos de eventos" />;
}

function SoeFiles() {
  const regs = useRegulatorOptions();
  const { data: me } = useMe();
  const [regulatorId, setRegulatorId] = useState("");
  const list = useQuery({
    queryKey: ["soe-files", regulatorId],
    queryFn: () => api.get<{ files: SoeFileRow[] }>(`/api/soe/files${regulatorId ? `?regulatorId=${regulatorId}` : ""}`),
  });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirm, setConfirm] = useState(false);
  const deletable = (list.data?.files ?? []).filter((f) => f.can_delete);
  // Seleção só do que está na tela (trocar o filtro não apaga o que sumiu da lista).
  const chosen = deletable.filter((f) => selected.has(f.id));
  const allChosen = deletable.length > 0 && chosen.length === deletable.length;
  const toggle = (id: number) => setSelected((s) => (s.has(id) ? new Set([...s].filter((x) => x !== id)) : new Set(s).add(id)));

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select aria-label="Regulador" value={regulatorId} onChange={(e) => setRegulatorId(e.target.value)} className="w-auto! min-w-64">
          <option value="">Todos os reguladores</option>
          {regs.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </Select>
        <div className="ml-auto flex gap-2">
          {chosen.length > 0 && (
            <Button variant="danger" onClick={() => setConfirm(true)}>
              <Trash2 className="size-4" aria-hidden /> Apagar {chosen.length} {chosen.length === 1 ? "arquivo" : "arquivos"}
            </Button>
          )}
          {canManageAnything(me) && (
            <Link
              to="/eventos/lixeira"
              className="inline-flex h-10 items-center gap-2 rounded-md border border-line bg-surface px-4 text-sm font-medium hover:bg-surface-2"
            >
              <Trash2 className="size-4" aria-hidden /> Lixeira
            </Link>
          )}
        </div>
      </div>
      {confirm && (
        <DeleteFilesDialog
          kind={EVT_TRASH}
          ids={chosen.map((f) => f.id)}
          files={chosen.map((f) => f.name)}
          onClose={() => setConfirm(false)}
          onDone={() => {
            setConfirm(false);
            setSelected(new Set());
          }}
        />
      )}
      {list.isLoading ? (
        <Spinner />
      ) : !list.data?.files.length ? (
        <EmptyState title="Nenhum arquivo de eventos recebido" />
      ) : (
        <Table>
          <thead>
            <tr>
              {deletable.length > 0 && (
                <th className="w-10">
                  <input
                    type="checkbox"
                    aria-label="Selecionar todos"
                    checked={allChosen}
                    onChange={() => setSelected(allChosen ? new Set() : new Set(deletable.map((f) => f.id)))}
                    className="accent-[var(--brand)]"
                  />
                </th>
              )}
              <th>Período (relógio do regulador)</th>
              <th>Regulador</th>
              <th className="text-right">Eventos</th>
              <th className="text-right">Ativações</th>
              <th>Arquivo</th>
            </tr>
          </thead>
          <tbody>
            {list.data.files.map((f) => (
              <tr key={f.id} className={cx("hover:bg-surface-2", selected.has(f.id) && "bg-danger/5")}>
                {deletable.length > 0 && (
                  <td>
                    {f.can_delete && (
                      <input
                        type="checkbox"
                        aria-label={`Selecionar ${f.name}`}
                        checked={selected.has(f.id)}
                        onChange={() => toggle(f.id)}
                        className="accent-[var(--brand)]"
                      />
                    )}
                  </td>
                )}
                <td className="whitespace-nowrap">
                  <Link to={`/eventos/${f.id}`} className="font-medium hover:underline">
                    {f.first_ts ? formatDateTimeMs(f.first_ts) : "sem hora válida"}
                  </Link>
                  {f.first_ts && f.last_ts && f.last_ts !== f.first_ts && <p className="text-xs text-muted">até {formatDateTimeMs(f.last_ts)}</p>}
                  {!f.first_ts && <p className="text-xs text-muted">recebido {new Date(f.received_at).toLocaleString("pt-BR")}</p>}
                  {f.first_ts && f.timed < f.events && <p className="text-xs text-warn">{f.events - f.timed} sem hora válida</p>}
                </td>
                <td className="text-sm text-muted">
                  {f.site_name} › {f.unit_name} › {f.regulator_name}
                </td>
                <td className="text-right tabular-nums">{f.decode_error ? <Badge tone="danger">erro</Badge> : f.events}</td>
                <td className="text-right tabular-nums">{f.activations}</td>
                <td>
                  <code className="font-mono text-xs text-muted">{f.name}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

interface SoeFileDetail {
  id: number;
  name: string;
  receivedAt: string;
  decodeError: string | null;
  regulator: { id: number; name: string; unit: string; site: string };
  clockValid: boolean;
  events: {
    id: number;
    line_no: number;
    ts: string | null;
    device_time: string | null;
    number: number;
    tag: string;
    description: string;
    state: boolean | null;
    raw: string;
  }[];
  oscillographies: { id: number; name: string; start_ts: string }[];
  canDelete: boolean;
}

export function SoeFilePage() {
  const fileId = Number(useParams().id);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { data: f, isLoading, error } = useQuery({
    queryKey: ["soe-file", fileId],
    queryFn: () => api.get<SoeFileDetail>(`/api/soe/files/${fileId}`),
  });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (f?.events ?? []).filter(
      (e) =>
        (!onlyActive || e.state === true) &&
        (!q || e.description.toLowerCase().includes(q) || e.tag.toLowerCase().includes(q) || String(e.number) === q),
    );
  }, [f, search, onlyActive]);

  if (isLoading) return <Spinner />;
  if (error || !f) return <EmptyState title="Arquivo de eventos não encontrado" />;
  const untimed = f.events.filter((e) => e.ts === null).length;

  return (
    <>
      <Link to="/eventos" className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-fg">
        <ArrowLeft className="size-4" aria-hidden /> Sequência de eventos
      </Link>
      <PageHeader
        title={`Arquivo ${f.name}`}
        description={`${f.regulator.site} › ${f.regulator.unit} › ${f.regulator.name}`}
        actions={
          f.canDelete && (
            <Button variant="secondary" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="size-4" aria-hidden /> Apagar
            </Button>
          )
        }
      />
      {confirmDelete && (
        <DeleteFilesDialog kind={EVT_TRASH} ids={[f.id]} files={[f.name]} onClose={() => setConfirmDelete(false)} onDone={() => navigate("/eventos")} />
      )}
      <div className="mb-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted">
        <span className="flex items-center gap-1.5">
          <FileText className="size-4" aria-hidden /> {f.events.length} eventos · {f.events.filter((e) => e.state).length} ativações
        </span>
        <span className="flex items-center gap-1.5">
          <Clock className="size-4" aria-hidden /> recebido {new Date(f.receivedAt).toLocaleString("pt-BR")}
        </span>
      </div>

      <div className="mb-4 space-y-2">
        {f.decodeError && <Alert kind="error">O arquivo não pôde ser decodificado: {f.decodeError}</Alert>}
        {untimed > 0 && (
          <Alert>
            {untimed === f.events.length ? "Todos os eventos" : `${untimed} de ${f.events.length} eventos`} deste arquivo foram gravados com o
            relógio do regulador sem hora válida. A ordem abaixo é a do arquivo, que é a ordem em que aconteceram; o instante desses eventos
            é desconhecido.
          </Alert>
        )}
        {f.oscillographies.length > 0 && (
          <Card className="flex flex-wrap items-center gap-2 p-3 text-sm">
            <Waves className="size-4 text-muted" aria-hidden />
            Oscilografias no mesmo intervalo:
            {f.oscillographies.map((o) => (
              <Link key={o.id} to={`/oscilografias/${o.id}`} className="font-medium text-brand hover:underline">
                {formatDateTimeMs(o.start_ts)}
              </Link>
            ))}
          </Card>
        )}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Input
          placeholder="Buscar evento ou número"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64!"
          aria-label="Buscar evento"
        />
        <label className="flex items-center gap-2 text-sm text-muted">
          <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} className="accent-[var(--brand)]" />
          só ativações
        </label>
        {rows.length !== f.events.length && (
          <span className="text-xs text-muted">
            {rows.length} de {f.events.length}
          </span>
        )}
      </div>

      {rows.length === 0 ? (
        <EmptyState title="Nenhum evento com esse filtro" />
      ) : (
        <Table>
          <thead>
            <tr>
              <th className="w-16 text-right">Linha</th>
              <th>Instante (relógio do regulador)</th>
              <th>Evento</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id} className={cx(e.state === true && "bg-warn/5")}>
                <td className="text-right font-mono text-xs text-muted tabular-nums">{e.line_no}</td>
                <td className="whitespace-nowrap font-mono text-xs tabular-nums">
                  {e.ts ? (
                    formatDateTimeMs(e.ts)
                  ) : (
                    <span className="flex items-center gap-1 text-muted" title={e.device_time ?? "sem hora"}>
                      <AlertTriangle className="size-3.5" aria-hidden /> sem hora válida
                    </span>
                  )}
                </td>
                <td>
                  {e.number < 0 ? (
                    <span className="text-muted">
                      linha ilegível: <code className="font-mono text-xs">{e.raw}</code>
                    </span>
                  ) : (
                    <>
                      {e.description} <code className="ml-1 font-mono text-xs text-muted">{e.tag || `#${e.number}`}</code>
                    </>
                  )}
                </td>
                <td>{e.state === null ? "—" : e.state ? <Badge tone="warn">ativou</Badge> : <Badge>normalizou</Badge>}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

// ── Busca por período ────────────────────────────────────────────────────────

function SoeSearch() {
  const [tab, setTab] = useState<"timed" | "untimed">("timed");
  const [period, setPeriod] = useState("7d");
  const [regulatorId, setRegulatorId] = useState("");
  const [search, setSearch] = useState("");
  const p = PERIODS.find((x) => x.key === period)!;
  const regs = useRegulatorOptions();

  const timed = useQuery({
    queryKey: ["soe", period, regulatorId, search],
    queryFn: () => {
      const to = new Date();
      const from = new Date(to.getTime() - p.ms);
      const qs = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
      if (regulatorId) qs.set("regulatorId", regulatorId);
      if (search.trim()) qs.set("search", search.trim());
      return api.get<{ events: Row[]; untimed: { n: number; files: number } }>(`/api/soe?${qs}`);
    },
    placeholderData: (prev) => prev,
  });
  const untimed = useQuery({
    queryKey: ["soe-untimed", regulatorId],
    queryFn: () => api.get<{ events: Row[] }>(`/api/soe/untimed${regulatorId ? `?regulatorId=${regulatorId}` : ""}`),
    enabled: tab === "untimed",
  });

  const nUntimed = timed.data?.untimed.n ?? 0;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-line bg-surface p-0.5" role="tablist">
          <TabBtn on={tab === "timed"} onClick={() => setTab("timed")}>
            Com hora
          </TabBtn>
          <TabBtn on={tab === "untimed"} onClick={() => setTab("untimed")}>
            Sem hora válida {nUntimed > 0 && <span className="ml-1 text-xs">({nUntimed})</span>}
          </TabBtn>
        </div>
        <Select aria-label="Regulador" value={regulatorId} onChange={(e) => setRegulatorId(e.target.value)} className="w-auto! min-w-56">
          <option value="">Todos os reguladores</option>
          {regs.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </Select>
        {tab === "timed" && (
          <>
            <div className="inline-flex rounded-md border border-line bg-surface p-0.5" role="group" aria-label="Período">
              {PERIODS.map((x) => (
                <TabBtn key={x.key} on={x.key === period} onClick={() => setPeriod(x.key)}>
                  {x.label}
                </TabBtn>
              ))}
            </div>
            <Input placeholder="Buscar evento" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56!" aria-label="Buscar evento" />
          </>
        )}
      </div>

      {tab === "timed" ? (
        timed.isLoading ? (
          <Spinner />
        ) : !timed.data?.events.length ? (
          <EmptyState title="Nenhum evento no período">
            {nUntimed > 0 && `Há ${nUntimed} eventos sem hora válida — veja a aba ao lado.`}
          </EmptyState>
        ) : (
          <div className={cx("transition-opacity", timed.isFetching && "opacity-80")}>
            <EventTable rows={timed.data.events} timed />
          </div>
        )
      ) : untimed.isLoading ? (
        <Spinner />
      ) : (
        <>
          <div className="mb-3">
            <Alert>
              O relógio do regulador estava sem hora válida quando estes eventos foram gravados. Eles aparecem na ordem em que estão no
              arquivo, que é a ordem em que aconteceram; o instante é desconhecido.
            </Alert>
          </div>
          {untimed.data?.events.length ? <EventTable rows={untimed.data.events} timed={false} /> : <EmptyState title="Nenhum evento sem hora" />}
        </>
      )}
    </>
  );
}

function TabBtn({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cx("rounded px-3 py-1.5 text-sm", on ? "bg-brand text-white" : "text-muted hover:bg-surface-2 hover:text-fg")}
    >
      {children}
    </button>
  );
}

function EventTable({ rows, timed }: { rows: Row[]; timed: boolean }) {
  return (
    <Table>
      <thead>
        <tr>
          <th>{timed ? "Instante (relógio do regulador)" : "Arquivo · linha"}</th>
          <th>Evento</th>
          <th>Estado</th>
          <th>Onde</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td className="whitespace-nowrap font-mono text-xs tabular-nums">{timed ? r.device_time : `${r.file} · ${r.line_no}`}</td>
            <td>
              {r.number < 0 ? (
                <span className="text-muted">
                  linha ilegível: <code className="font-mono text-xs">{r.raw}</code>
                </span>
              ) : (
                <>
                  {r.description} <code className="ml-1 font-mono text-xs text-muted">{r.tag || `#${r.number}`}</code>
                </>
              )}
            </td>
            <td>{r.state === null ? "—" : r.state ? <Badge tone="warn">ativou</Badge> : <Badge>normalizou</Badge>}</td>
            <td className="text-sm text-muted">
              {r.site_name} › {r.unit_name} › {r.regulator_name}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
